"use client";

import { IconHeading } from "@/components/platform/v2/IconHeading";
import Link from "next/link";
import { ArrowRight, Megaphone, Repeat, Rocket, Users } from "lucide-react";
import { Section, SectionHeading } from "@/components/platform/v2/Section";
import { PageCta, PageHero } from "@/components/platform/v2/PageEdge";
import { CASES } from "@/data/cases";

const directions = [
  [
    Rocket,
    "Запуск цифрового канала",
    "Сформировать предложение и понятные точки входа.",
  ],
  [
    Users,
    "Перевод существующей аудитории",
    "Дать действующим клиентам причину перейти в собственный канал.",
  ],
  [
    Megaphone,
    "Привлечение новых клиентов",
    "Подключить продвижение под задачу проекта.",
  ],
  [
    Repeat,
    "Возврат, лояльность и рефералы",
    "Продолжать работу после первого контакта.",
  ],
] as const;
const chain = [
  "Источник",
  "Клиент",
  "CRM",
  "Сегмент",
  "Коммуникация",
  "Предложение",
  "Повторная продажа",
];
const cases = CASES.filter((c) =>
  ["business-forum", "groverman", "private-clinic"].includes(c.slug),
);
const Promotion = () => (
  <div className="vcentre-public pt-24 sm:pt-28 md:pt-32">
    <PageHero
      eyebrow="Продвижение"
      title={
        <>
          Запустить — только половина задачи.
          <br />
          <span className="text-primary">
            Важно продолжить работу с клиентом.
          </span>
        </>
      }
      text={
        <p>
          Помогаем привести аудиторию в собственные каналы бизнеса и связать
          первый контакт с CRM, коммуникациями и повторными продажами.
        </p>
      }
      actions={
        <>
          <Link className="btn-primary" href="/signin?focus=promotion">
            Разобрать моё продвижение
          </Link>
          <a href="#directions" className="btn-ghost">
            Как работает продвижение
          </a>
        </>
      }
      visual={
        <img
          src={"/assets/images/platform/promotion-mascot-hero.png"}
          alt="Утёнок VCENTRE с ракетой и фразой «Запускаем быстро»"
          width={1254}
          height={1254}
          loading="eager"
          className="w-full max-w-[330px] animate-float object-contain drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)] sm:max-w-[420px]"
        />
      }
    />
    <Section tone="quiet">
      <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
        <div>
          <p className="eyebrow">Почему запуска недостаточно</p>
          <h2 className="display-2 mt-4">
            Канал начинает работать, когда клиент понимает его пользу.
          </h2>
          <p className="lede mt-5">
            Недостаточно открыть сайт или приложение. Нужны понятная причина
            перейти, удобный первый шаг и дальнейшая работа с человеком внутри
            системы.
          </p>
        </div>
        <div className="border-y border-border/60 py-6">
          {[
            "Запустили",
            "Рассказали",
            "Привели",
            "Сохранили в CRM",
            "Вернули",
          ].map((x, i) => (
            <div
              key={x}
              className="flex items-center gap-4 border-b border-border/50 py-3 last:border-0"
            >
              <span className="text-xs font-bold text-primary">0{i + 1}</span>
              <b>{x}</b>
            </div>
          ))}
        </div>
      </div>
    </Section>
    <Section id="directions">
      <SectionHeading
        eyebrow="Чем помогаем"
        title="Четыре направления продвижения"
      />
      <div className="mt-10 grid gap-6 md:grid-cols-2">
        {directions.map(([Icon, title, text], i) => (
          <article key={title} className="border-t border-primary/50 py-6">
            <span className="mb-3 block text-xs font-bold text-primary">
              0{i + 1}
            </span>
            <IconHeading
              as="h3"
              icon={<Icon className="h-6 w-6 text-primary" />}
              className="display-3"
            >
              {title}
            </IconHeading>
            <p className="body-text mt-3">{text}</p>
          </article>
        ))}
      </div>
      <div className="mt-8 text-center">
        <Link href="/signin?focus=promotion" className="btn-link">
          Собрать план продвижения
        </Link>
      </div>
    </Section>
    <Section tone="quiet">
      <SectionHeading
        eyebrow="После первого контакта"
        title="Маркетинг продолжается внутри VCENTRE.ONE"
      />
      <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-7">
        {chain.map((x, i) => (
          <div
            key={x}
            className={`rounded-xl border p-4 text-sm font-bold ${i === chain.length - 1 ? "border-primary/50 text-primary" : "border-border/60"}`}
          >
            {x}
          </div>
        ))}
      </div>
    </Section>
    <Section>
      <SectionHeading eyebrow="Практика" title="Реальные сценарии" />
      <div className="mt-10 grid gap-5 md:grid-cols-3">
        {cases.map((c) => (
          <Link key={c.slug} href="/platform/cases" className="surface surface-hover">
            <p className="eyebrow">{c.category}</p>
            <h3 className="mt-3 text-xl font-bold">{c.client}</h3>
            <p className="body-text mt-3">{c.shortDescription}</p>
            <span className="btn-link mt-5">
              Смотреть кейсы <ArrowRight className="h-4 w-4" />
            </span>
          </Link>
        ))}
      </div>
    </Section>
    <Section>
      <PageCta
        title="Разберём путь клиента после запуска"
        text={
          <p>
            Покажем, как связать привлечение, собственные каналы и дальнейшую
            работу в VCENTRE.ONE.
          </p>
        }
        actions={
          <Link
            href="/signin?focus=promotion&source=promotion-bottom"
            className="btn-primary"
          >
            Показать, что мы продвигаем
          </Link>
        }
        visual={
          <img
            src={"/assets/images/platform/promotion-mascot-cta.png"}
            alt="Утёнок VCENTRE с фразой «Всё связано»"
            width={1254}
            height={1254}
            loading="lazy"
            className="w-full max-w-[260px] object-contain drop-shadow-[0_24px_48px_rgba(0,0,0,0.55)]"
          />
        }
      />
    </Section>
  </div>
);
export default Promotion;
